import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Search,
  RefreshCw,
  Download,
  Mail,
  Phone,
  Package,
  DollarSign,
  Calendar,
  ChevronDown,
  ChevronUp,
  Eye,
  X,
  User
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';

const CuratorPrintLabCustomers = () => {
  const { selectedOrganization } = useOutletContext() || {};
  const orgId = selectedOrganization?.organization_id;

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('order_count');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [stats, setStats] = useState({ totalCustomers: 0, activeThisYear: 0, totalOrders: 0, totalRevenue: 0 });

  const fetchCustomers = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      // Fetch all orders for this org and aggregate client-side
      // (Supabase JS doesn't support GROUP BY, so we aggregate here)
      const { data, error } = await supabase
        .from('service_orders')
        .select('customer_email, customer_name, customer_phone, status, final_price, quoted_price, created_at, service_type')
        .eq('organization_id', orgId);

      if (error) throw error;

      // Aggregate by email (primary key for customers)
      const map = new Map();
      (data || []).forEach(order => {
        const email = (order.customer_email || '').toLowerCase().trim();
        if (!email) return;
        const existing = map.get(email) || {
          email,
          name: order.customer_name || '',
          phone: order.customer_phone || '',
          order_count: 0,
          total_spend: 0,
          first_order: order.created_at,
          last_order: order.created_at,
          services: new Set(),
          statuses: {},
        };
        existing.order_count++;
        const price = parseFloat(order.final_price || order.quoted_price || 0);
        existing.total_spend += price;
        if (order.created_at < existing.first_order) existing.first_order = order.created_at;
        if (order.created_at > existing.last_order) existing.last_order = order.created_at;
        if (order.service_type) existing.services.add(order.service_type);
        existing.statuses[order.status] = (existing.statuses[order.status] || 0) + 1;
        // Keep the most recent non-empty name/phone
        if (order.customer_name && order.created_at >= existing.last_order) {
          existing.name = order.customer_name;
        }
        if (order.customer_phone && !existing.phone) {
          existing.phone = order.customer_phone;
        }
        map.set(email, existing);
      });

      const aggregated = Array.from(map.values()).map(c => ({
        ...c,
        services: Array.from(c.services),
      }));

      setCustomers(aggregated);

      // Stats
      const thisYear = new Date().getFullYear();
      const activeThisYear = aggregated.filter(c => new Date(c.last_order).getFullYear() === thisYear).length;
      setStats({
        totalCustomers: aggregated.length,
        activeThisYear,
        totalOrders: data.length,
        totalRevenue: aggregated.reduce((sum, c) => sum + c.total_spend, 0),
      });
    } catch (err) {
      console.error('Failed to fetch customers:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  // Fetch orders for a specific customer
  const openCustomerDetail = async (customer) => {
    setSelectedCustomer(customer);
    setLoadingOrders(true);
    try {
      const { data, error } = await supabase
        .from('service_orders')
        .select('*')
        .eq('organization_id', orgId)
        .ilike('customer_email', customer.email)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setCustomerOrders(data || []);
    } catch (err) {
      console.error('Failed to fetch customer orders:', err);
    } finally {
      setLoadingOrders(false);
    }
  };

  // Sort and filter
  const filtered = customers
    .filter(c => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q) || (c.phone || '').includes(q);
    })
    .sort((a, b) => {
      let aVal = a[sortBy], bVal = b[sortBy];
      if (sortBy === 'last_order' || sortBy === 'first_order') {
        aVal = new Date(aVal); bVal = new Date(bVal);
      }
      if (typeof aVal === 'string') { aVal = aVal.toLowerCase(); bVal = (bVal || '').toLowerCase(); }
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortOrder('desc');
    }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return null;
    return sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
  const formatMoney = (n) => n > 0 ? `$${n.toFixed(2)}` : '—';

  // CSV export
  const exportCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Orders', 'Total Spend', 'First Order', 'Last Order', 'Services'];
    const rows = filtered.map(c => [
      c.name, c.email, c.phone, c.order_count, c.total_spend.toFixed(2),
      formatDate(c.first_order), formatDate(c.last_order), c.services.join('; ')
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `printlab-customers-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const containerStyle = { padding: '2rem', maxWidth: '1400px', margin: '0 auto', fontFamily: 'system-ui, -apple-system, sans-serif' };
  const cardStyle = { background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '1.5rem' };
  const statCardStyle = { ...cardStyle, textAlign: 'center', flex: '1', minWidth: '150px' };
  const btnStyle = { display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '0.875rem', color: '#374151' };
  const inputStyle = { width: '100%', padding: '0.75rem 1rem 0.75rem 2.5rem', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.875rem', outline: 'none' };
  const thStyle = { padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };
  const tdStyle = { padding: '0.75rem 1rem', fontSize: '0.875rem', color: '#374151', borderTop: '1px solid #f3f4f6' };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827', margin: 0 }}>Customers</h1>
          <p style={{ color: '#6b7280', margin: '0.25rem 0 0', fontSize: '0.875rem' }}>Aggregated from service order history</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={exportCSV} style={btnStyle}><Download size={16} /> Export</button>
          <button onClick={fetchCustomers} style={btnStyle}><RefreshCw size={16} /> Refresh</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div style={statCardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', color: '#6b7280', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
            <User size={16} /> Total Customers
          </div>
          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#111827' }}>{stats.totalCustomers}</div>
        </div>
        <div style={statCardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', color: '#6b7280', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
            <Calendar size={16} /> Active This Year
          </div>
          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#3b82f6' }}>{stats.activeThisYear}</div>
        </div>
        <div style={statCardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', color: '#6b7280', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
            <Package size={16} /> Total Orders
          </div>
          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#8b5cf6' }}>{stats.totalOrders}</div>
        </div>
        <div style={statCardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', color: '#6b7280', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
            <DollarSign size={16} /> Revenue
          </div>
          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#10b981' }}>{formatMoney(stats.totalRevenue)}</div>
        </div>
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: '1.5rem' }}>
        <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
        <input
          type="text"
          placeholder="Search by name, email, or phone..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={inputStyle}
        />
      </div>

      {/* Table */}
      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>Loading customers...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>No customers found</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={thStyle} onClick={() => handleSort('name')}>Customer <SortIcon col="name" /></th>
                  <th style={thStyle} onClick={() => handleSort('order_count')}>Orders <SortIcon col="order_count" /></th>
                  <th style={thStyle} onClick={() => handleSort('total_spend')}>Total Spend <SortIcon col="total_spend" /></th>
                  <th style={thStyle} onClick={() => handleSort('last_order')}>Last Order <SortIcon col="last_order" /></th>
                  <th style={thStyle} onClick={() => handleSort('first_order')}>First Order <SortIcon col="first_order" /></th>
                  <th style={thStyle}>Services</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c, i) => (
                  <tr key={c.email} style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: '600', color: '#111827' }}>{c.name || '(no name)'}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <Mail size={12} /> {c.email}
                      </div>
                      {c.phone && (
                        <div style={{ fontSize: '0.75rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <Phone size={12} /> {c.phone}
                        </div>
                      )}
                    </td>
                    <td style={{ ...tdStyle, fontWeight: '600', fontSize: '1rem' }}>{c.order_count}</td>
                    <td style={{ ...tdStyle, fontWeight: '600', color: c.total_spend > 0 ? '#059669' : '#9ca3af' }}>{formatMoney(c.total_spend)}</td>
                    <td style={tdStyle}>{formatDate(c.last_order)}</td>
                    <td style={tdStyle}>{formatDate(c.first_order)}</td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                        {c.services.map(s => (
                          <span key={s} style={{ padding: '0.125rem 0.5rem', borderRadius: '9999px', background: '#f3f4f6', fontSize: '0.7rem', color: '#374151', textTransform: 'capitalize' }}>
                            {s.replace('_', ' ')}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <button onClick={() => openCustomerDetail(c)} style={{ ...btnStyle, padding: '0.375rem 0.75rem', fontSize: '0.8rem' }}>
                        <Eye size={14} /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ marginTop: '1rem', textAlign: 'center', fontSize: '0.8rem', color: '#9ca3af' }}>
        Showing {filtered.length} of {customers.length} customers
      </div>

      {/* Customer Detail Modal */}
      {selectedCustomer && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
             onClick={() => setSelectedCustomer(null)}>
          <div style={{ ...cardStyle, width: '90%', maxWidth: '900px', maxHeight: '85vh', overflow: 'auto' }}
               onClick={e => e.stopPropagation()}>
            {/* Modal header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: '#111827' }}>
                  {selectedCustomer.name || selectedCustomer.email}
                </h2>
                <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}><Mail size={14} /> {selectedCustomer.email}</span>
                  {selectedCustomer.phone && <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}><Phone size={14} /> {selectedCustomer.phone}</span>}
                </div>
              </div>
              <button onClick={() => setSelectedCustomer(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
                <X size={20} />
              </button>
            </div>

            {/* Summary cards */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
              <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '1rem', flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Orders</div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827' }}>{selectedCustomer.order_count}</div>
              </div>
              <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '1rem', flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Total Spend</div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#059669' }}>{formatMoney(selectedCustomer.total_spend)}</div>
              </div>
              <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '1rem', flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Customer Since</div>
                <div style={{ fontSize: '1rem', fontWeight: '600', color: '#111827' }}>{formatDate(selectedCustomer.first_order)}</div>
              </div>
              <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '1rem', flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Services</div>
                <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>{selectedCustomer.services.map(s => s.replace('_', ' ')).join(', ')}</div>
              </div>
            </div>

            {/* Order history */}
            <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#111827', marginBottom: '0.75rem' }}>Order History</h3>
            {loadingOrders ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Loading orders...</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb' }}>
                      <th style={thStyle}>Order #</th>
                      <th style={thStyle}>Date</th>
                      <th style={thStyle}>Service</th>
                      <th style={thStyle}>Details</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerOrders.map(o => (
                      <tr key={o.id}>
                        <td style={tdStyle}><span style={{ fontWeight: '600', color: '#4f46e5' }}>{o.order_num || '—'}</span></td>
                        <td style={tdStyle}>{formatDate(o.created_at)}</td>
                        <td style={tdStyle}><span style={{ textTransform: 'capitalize' }}>{(o.service_type || '').replace('_', ' ')}</span>{o.service_tier ? ` (${o.service_tier})` : ''}</td>
                        <td style={tdStyle}>{[o.paper_type, o.print_dimensions].filter(Boolean).join(' · ') || '—'}</td>
                        <td style={tdStyle}>
                          <span style={{
                            padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '600',
                            background: o.status === 'paid' ? '#f0fdf4' : o.status === 'delivered' ? '#d1fae5' : '#f3f4f6',
                            color: o.status === 'paid' ? '#22c55e' : o.status === 'delivered' ? '#059669' : '#374151',
                          }}>
                            {(o.status || '').replace('_', ' ')}
                          </span>
                        </td>
                        <td style={tdStyle}>{formatMoney(parseFloat(o.final_price || o.quoted_price || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CuratorPrintLabCustomers;
